// Lightweight localStorage-backed state for the GCBC prototype.
// Lets demo actions (approve, reject, decrypt, suspend) persist across page
// reloads so a stakeholder walkthrough doesn't reset between clicks.
//
// Bump KEY when the data shape changes to invalidate old entries.

import { useEffect, useState } from "react";

const KEY = "gcbc:demo:v1";

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeAll(state) {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota / SSR */ }
}

/**
 * usePersistedState — drop-in replacement for useState whose value is keyed by
 * a namespace + id and survives page reloads. Falls back to `initial` if no
 * persisted value exists.
 *
 *   const [cards, setCards] = usePersistedState(`submission:${id}:cards`, initialCards);
 */
export function usePersistedState(key, initial) {
  const [value, setValue] = useState(() => {
    const all = readAll();
    return key in all ? all[key] : initial;
  });

  useEffect(() => {
    const all = readAll();
    all[key] = value;
    writeAll(all);
  }, [key, value]);

  return [value, setValue];
}

/** Wipe ALL demo overrides. Reloads page so every component re-reads defaults. */
export function resetDemoState() {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
  window.location.reload();
}

/** Read-only count of stored overrides (used by the Reset link badge). */
export function demoOverrideCount() {
  return Object.keys(readAll()).length;
}
